import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const Z = 10055;

const SensaMark = ({ size = 30 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 4.2v2.7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <circle cx="12" cy="3.2" r="1.2" fill="currentColor" />
    <rect x="3.9" y="6.8" width="16.2" height="13.6" rx="5.2" stroke="currentColor" strokeWidth="2" />
    <circle cx="9" cy="13.6" r="1.8" fill="currentColor" />
    <circle cx="15" cy="13.6" r="1.8" fill="currentColor" />
  </svg>
);

const Check = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M4 10.5L8 14.5L16 6" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <line x1="6" y1="6" x2="18" y2="18" />
    <line x1="18" y1="6" x2="6" y2="18" />
  </svg>
);

const gradientButton =
  "px-[18px] py-[9px] rounded-lg bg-gradient-to-r from-[#A04A97] to-[#C95EBE] text-white text-[14px] font-semibold shadow-[0_2px_8px_rgba(160,74,151,0.35)] hover:opacity-95";
const outlineButton =
  "px-4 py-[9px] rounded-lg border border-[#E5E7EB] text-[14px] font-medium text-[#374151] hover:bg-[#F9FAFB]";

// autoFocus would scroll a tall modal to its bottom button on small screens.
const useQuietFocus = () => {
  const ref = useRef(null);
  useEffect(() => {
    ref.current?.focus({ preventScroll: true });
  }, []);
  return ref;
};

const useEscape = (onClose) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
};

const Overlay = ({ children, blur = 3, dark = 0.5 }) =>
  createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center p-4 font-outfit"
      style={{ zIndex: Z, background: `rgba(20,10,26,${dark})`, backdropFilter: `blur(${blur}px)` }}
    >
      {children}
    </div>,
    document.body
  );

export const WelcomeModal = ({ firstName, onStart, onSkip }) => {
  const focusRef = useQuietFocus();
  useEscape(onSkip);
  return (
    <Overlay>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="ds-welcome-title"
        className="w-full max-w-[880px] max-h-[calc(100vh-32px)] overflow-y-auto flex flex-col md:flex-row rounded-[20px] bg-white shadow-[0_30px_80px_-20px_rgba(20,10,26,0.6)]"
      >
        <div className="relative md:w-[360px] flex-none bg-[#2C1338] px-9 py-10 flex flex-col overflow-hidden">
          <span className="absolute -left-[120px] -top-20 w-[360px] h-[360px] rounded-full bg-[radial-gradient(circle,rgba(160,74,151,0.55),transparent_68%)] blur-[40px]" />
          <span className="absolute -right-[140px] -bottom-[120px] w-[380px] h-[380px] rounded-full bg-[radial-gradient(circle,rgba(243,144,79,0.4),transparent_68%)] blur-[50px]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle,rgba(255,255,255,0.12)_1px,transparent_1px)] [background-size:22px_22px]" />
          <span className="relative w-14 h-14 rounded-full bg-gradient-to-br from-[#B85BAE] via-[#A04A97] to-[#C86B4F] text-white flex items-center justify-center shadow-[0_8px_24px_-6px_rgba(160,74,151,0.8)]">
            <SensaMark />
          </span>
          <p className="relative mt-7 text-[15px] font-medium text-[#C9A9D6]">
            {firstName ? `Welcome, ${firstName}.` : "Welcome to DocuSensa."}
          </p>
          <h2 id="ds-welcome-title" className="relative mt-2 text-[34px] leading-[1.1] font-bold tracking-[-0.02em] text-white [text-wrap:balance]">
            Let's set up your{" "}
            <span className="bg-gradient-to-r from-[#E7A3DE] to-[#F3904F] bg-clip-text text-transparent">workspace.</span>
          </h2>
          <p className="relative mt-3.5 text-[15px] leading-[1.55] text-white/70">
            A quick product tour, then we'll set up your first RFP as a project.
          </p>
          <div className="relative mt-8 md:mt-auto flex flex-col gap-3">
            {["Product tour", "Your first project", "Bring in your team"].map((label, i) => (
              <div key={label} className={`flex items-center gap-2.5 text-[13.5px] ${i === 0 ? "text-white" : "text-white/55"}`}>
                <span
                  className={`w-[22px] h-[22px] rounded-full flex items-center justify-center text-[11px] font-semibold ${
                    i === 0 ? "bg-[#A04A97]" : "border border-white/30"
                  }`}
                >
                  {i + 1}
                </span>
                {label}
              </div>
            ))}
          </div>
        </div>
        <div className="flex-1 px-8 md:px-10 py-9 flex flex-col min-w-0">
          <p className="text-[11px] font-semibold tracking-[0.09em] uppercase text-[#A04A97]">Getting started</p>
          <h3 className="mt-2 text-[24px] font-bold tracking-[-0.01em] text-[#18101C]">Here's what DocuSensa does for your bid team</h3>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              ["Sensa", "Reads RFPs, scores them and drafts answers."],
              ["Projects", "One place per bid, from setup to export."],
              ["Knowledge Base", "Your past proposals, reused with citations."],
              ["Tender Search", "Federal opportunities, filtered to your fit."],
            ].map(([title, text]) => (
              <div key={title} className="rounded-xl border border-[#E5E7EB] px-4 py-3.5">
                <p className="text-[15px] font-semibold text-[#18101C]">{title}</p>
                <p className="mt-1 text-[13px] leading-[1.4] text-[#77707E]">{text}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 md:mt-auto pt-6 flex flex-wrap items-center gap-3">
            <span className="flex-1 min-w-[140px] text-[13px] text-[#77707E]">Takes about a minute</span>
            <button type="button" onClick={onSkip} className={outlineButton}>
              Skip for now
            </button>
            <button type="button" onClick={onStart} className={gradientButton} ref={focusRef}>
              Start tour
            </button>
          </div>
        </div>
      </div>
    </Overlay>
  );
};

export const PageIntroModal = ({ intro, onPrimary, onLater, onNever }) => {
  const focusRef = useQuietFocus();
  const [dontShow, setDontShow] = useState(false);
  const dismiss = () => (dontShow ? onNever() : onLater());
  useEscape(dismiss);
  return (
    <Overlay blur={2} dark={0.4}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="ds-intro-title"
        className="w-full max-w-[1000px] max-h-[calc(100vh-32px)] overflow-y-auto flex flex-col md:flex-row rounded-2xl bg-white shadow-[0_20px_60px_rgba(0,0,0,0.25)]"
      >
        <div className="md:w-[340px] flex-none bg-[#FBF6FA] px-7 py-8 border-b md:border-b-0 md:border-r border-[rgba(160,74,151,0.12)]">
          <p className="text-[17px] font-semibold text-[#18101C]">Here's how {intro.name} works</p>
          <span className="block mt-2 w-11 h-[3px] rounded-full bg-[#A04A97]" />
          <div className="mt-7 flex flex-col">
            {intro.steps.map((s, i) => (
              <div key={s.n} className="flex gap-3.5">
                <div className="flex flex-col items-center">
                  <span className="w-7 h-7 rounded-full bg-[#A04A97] text-white text-[13px] font-semibold flex items-center justify-center flex-none">
                    {s.n}
                  </span>
                  {i < intro.steps.length - 1 && <span className="flex-1 w-0 border-l-2 border-dashed border-[rgba(160,74,151,0.35)] my-1.5" />}
                </div>
                <div className="pb-5 min-w-0">
                  <p className="mt-[3px] text-[15px] font-semibold text-[#18101C]">{s.t}</p>
                  <p className="mt-1 text-[13.5px] leading-[1.5] text-[#5B5563]">{s.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative flex-1 px-6 md:px-10 pt-8 pb-6 flex flex-col items-center min-w-0">
          <button type="button" onClick={dismiss} aria-label="Close" className="absolute right-5 top-5 text-[#767676] hover:text-[#18101C]">
            <CloseIcon />
          </button>
          <h2 id="ds-intro-title" className="mt-1 text-[30px] font-bold text-[#18101C]">
            {intro.name}
          </h2>
          <p className="mt-2 max-w-[470px] text-center text-[15px] leading-[1.5] text-[#5B5563]">{intro.tag}</p>
          <div className="relative mt-6 w-full max-w-[500px] min-h-[240px] flex-1 rounded-2xl bg-[radial-gradient(ellipse_at_50%_40%,rgba(160,74,151,0.12),transparent_70%)] flex items-center justify-center">
            <div className="absolute inset-0 rounded-2xl opacity-50 bg-[radial-gradient(circle,rgba(24,16,28,0.14)_1px,transparent_1px)] [background-size:18px_18px]" />
            <div className="relative w-[340px] max-w-[calc(100%-24px)] bg-white rounded-xl border border-[rgba(24,16,28,0.08)] shadow-[0_18px_40px_-20px_rgba(24,16,28,0.45)] px-4 py-3.5">
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className="text-[11px] font-semibold tracking-[0.08em] uppercase text-[#77707E] truncate">{intro.snipTitle}</span>
                <span className="text-[11px] font-semibold text-[#A04A97] bg-[#F5EDF4] rounded-full px-2 py-0.5 whitespace-nowrap">{intro.chip}</span>
              </div>
              {intro.rows.map((r) => (
                <div key={r.a} className="flex items-center justify-between gap-3 py-2 border-t border-[#F3F4F6] text-[13px]">
                  <span className="text-[#18101C] truncate">{r.a}</span>
                  <span className="text-[#77707E] font-medium whitespace-nowrap tabular-nums">{r.b}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-6 w-full flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 flex-1 min-w-[150px] text-[13px] text-[#767676] cursor-pointer">
              <input
                type="checkbox"
                checked={dontShow}
                onChange={(e) => setDontShow(e.target.checked)}
                className="w-4 h-4 accent-[#A04A97]"
              />
              Don't show again
            </label>
            <button type="button" onClick={dismiss} className={outlineButton}>
              Maybe later
            </button>
            <button type="button" onClick={onPrimary} className={gradientButton} ref={focusRef}>
              {intro.cta}
            </button>
          </div>
        </div>
      </div>
    </Overlay>
  );
};

const CONFETTI_COLORS = ["#A04A97", "#C95EBE", "#F3904F", "#C86B4F", "#E7A3DE", "#8B3F83"];
const CONFETTI = (() => {
  let seed = 7;
  const rnd = () => (seed = (seed * 9301 + 49297) % 233280) / 233280;
  return Array.from({ length: 40 }, (_, i) => {
    const a = rnd() * Math.PI * 2;
    const r = 200 + rnd() * 80;
    return {
      x: Math.round(Math.cos(a) * r),
      y: Math.round(Math.sin(a) * r * 0.9),
      w: 6 + Math.round(rnd() * 6),
      h: 10 + Math.round(rnd() * 8),
      rot: Math.round(rnd() * 180),
      bg: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    };
  });
})();

export const CelebrationModal = ({ projectName, doneCount, total, nextLabel, onClose }) => {
  const focusRef = useQuietFocus();
  useEscape(onClose);
  return (
    <Overlay blur={0} dark={0.45}>
      <div className="relative">
        <div aria-hidden="true" className="absolute left-1/2 top-1/2 motion-reduce:hidden">
          {CONFETTI.map((c, i) => (
            <span
              key={i}
              className="absolute rounded-[2px]"
              style={{ left: c.x, top: c.y, width: c.w, height: c.h, background: c.bg, transform: `rotate(${c.rot}deg)` }}
            />
          ))}
        </div>
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="ds-celebrate-title"
          className="relative w-[440px] max-w-[calc(100vw-32px)] box-border bg-white rounded-[20px] px-8 pt-9 pb-6 text-center shadow-[0_30px_80px_-20px_rgba(20,10,26,0.6)]"
        >
          <span className="mx-auto w-[68px] h-[68px] rounded-full bg-gradient-to-br from-[#B85BAE] via-[#A04A97] to-[#C86B4F] flex items-center justify-center shadow-[0_0_0_8px_rgba(160,74,151,0.12),0_12px_30px_-8px_rgba(160,74,151,0.8)]">
            <Check size={30} />
          </span>
          <h2 id="ds-celebrate-title" className="mt-5 text-[26px] font-bold tracking-[-0.01em] text-[#18101C]">
            Your first project is ready.
          </h2>
          <p className="mt-2 text-[15px] leading-[1.5] text-[#5B5563]">
            {projectName ? <b className="font-semibold text-[#18101C]">{projectName}</b> : "Your project"} has been created. Upload the RFP and Sensa will
            extract every requirement for you.
          </p>
          <div className="mt-5 px-3.5 py-3 rounded-xl bg-[#FBF6FA] text-left">
            <div className="flex justify-between text-[13px]">
              <span className="font-semibold text-[#18101C]">Getting started</span>
              <span className="text-[#A04A97] font-semibold tabular-nums">
                {doneCount} of {total}
              </span>
            </div>
            <div className="mt-2 h-1.5 rounded-full bg-[rgba(160,74,151,0.14)]">
              <div
                className="h-full rounded-full bg-gradient-to-r from-[#A04A97] to-[#C95EBE]"
                style={{ width: `${Math.round((doneCount / total) * 100)}%` }}
              />
            </div>
            {nextLabel && <p className="mt-2 text-[12.5px] text-[#77707E]">Up next: {nextLabel.toLowerCase()}</p>}
          </div>
          <div className="mt-5 flex gap-2.5">
            <button type="button" onClick={onClose} className={`${outlineButton} flex-1`}>
              Later
            </button>
            <button type="button" onClick={onClose} className={`${gradientButton} flex-[1.6]`} ref={focusRef}>
              Upload RFP
            </button>
          </div>
        </div>
      </div>
    </Overlay>
  );
};

export { Check, SensaMark };
